import { Link } from 'react-router-dom';
import { SECTIONS, SHARED_SECTIONS, NON_DIV_SECTIONS, findGuideSection } from './learnSections';

/**
 * LearnSidebar — Round 96v (Jul 4 2026).
 *
 * Persistent left-column section-nav used on `/learn` (index) AND
 * every individual guide page. Two operating modes:
 *
 *   mode="index"  — Sections list. `activeSectionId` (driven by an
 *                   IntersectionObserver in LearnIndex) highlights
 *                   whichever section is currently in the reader's
 *                   viewport. Clicking a section jumps to that
 *                   section's anchor on the same page.
 *
 *   mode="guide"  — Same sections list, but the section that owns
 *                   `currentGuidePath` is auto-highlighted AND
 *                   expanded to reveal every guide inside it. The
 *                   current guide is marked "→ You are here". Every
 *                   other section is a clickable link back to the
 *                   Learn index anchor.
 *
 * `heading` overrides the default label ("On this page" for index,
 * "Learn Guide Sections" for guides).
 */
export default function LearnSidebar({
  mode = 'index',
  activeSectionId = null,
  currentGuidePath = null,
  heading = null,
}) {
  const guideMatch = mode === 'guide' ? findGuideSection(currentGuidePath) : null;
  const activeId = mode === 'guide'
    ? (guideMatch ? guideMatch.section.id : null)
    : activeSectionId;

  const defaultHeading = mode === 'guide' ? 'Learn Guide Sections' : 'On this page';
  const headingText = heading || defaultHeading;

  const renderSection = (s, palette) => {
    const isActive = activeId === s.id;
    // In guide mode we expand the active section to show all its
    // posts (so the reader can jump straight to a sibling guide).
    const isExpanded = mode === 'guide' && isActive;

    const activeVioletCls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-md border-l-2 border-violet-500 bg-violet-500/15 text-sm font-semibold text-violet-800 dark:text-violet-200 transition-colors';
    const inactiveVioletCls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-md border-l-2 border-transparent hover:border-violet-400/60 hover:bg-violet-500/10 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors';
    const activeAmberCls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-md border-l-2 border-amber-500 bg-amber-500/15 text-sm font-semibold text-amber-800 dark:text-amber-200 transition-colors';
    const inactiveAmberCls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-md border-l-2 border-transparent hover:border-amber-400/60 hover:bg-amber-500/10 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors';
    const activeSkyCls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-md border-l-2 border-sky-500 bg-sky-500/15 text-sm font-semibold text-sky-800 dark:text-sky-200 transition-colors';
    const inactiveSkyCls = 'flex items-center gap-2 px-2.5 py-1.5 rounded-md border-l-2 border-transparent hover:border-sky-400/60 hover:bg-sky-500/10 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors';
    const activeCls = palette === 'amber' ? activeAmberCls : palette === 'sky' ? activeSkyCls : activeVioletCls;
    const inactiveCls = palette === 'amber' ? inactiveAmberCls : palette === 'sky' ? inactiveSkyCls : inactiveVioletCls;
    const cls = isActive ? activeCls : inactiveCls;

    // In guide mode, section labels navigate back to the Learn index
    // hash anchor. In index mode, they're same-page hash links.
    const sectionHref = mode === 'guide' ? `/learn#${s.id}` : `#${s.id}`;
    const isRouterLink = mode === 'guide';

    return (
      <li key={s.id}>
        {isRouterLink ? (
          <Link
            to={sectionHref}
            data-testid={`learn-sidebar-chip-${s.id}`}
            data-active={isActive ? 'true' : undefined}
            className={cls}
          >
            <span aria-hidden="true" className="text-base leading-none">{s.icon}</span>
            <span className="truncate">{s.short}</span>
            <span className="ml-auto text-[10px] text-muted-foreground">{s.posts.length}</span>
          </Link>
        ) : (
          <a
            href={sectionHref}
            data-testid={`learn-sidebar-chip-${s.id}`}
            data-active={isActive ? 'true' : undefined}
            className={cls}
          >
            <span aria-hidden="true" className="text-base leading-none">{s.icon}</span>
            <span className="truncate">{s.short}</span>
            <span className="ml-auto text-[10px] text-muted-foreground">{s.posts.length}</span>
          </a>
        )}
        {isExpanded && (
          <ul className="mt-1 mb-2 ml-4 pl-3 border-l border-border/60 space-y-1">
            {s.posts.map((p) => {
              const isCurrentGuide = p.to === currentGuidePath;
              return (
                <li key={p.to}>
                  {isCurrentGuide ? (
                    <div
                      data-testid={`learn-sidebar-you-are-here-${p.to.split('/').pop()}`}
                      className={
                        palette === 'amber'
                          ? 'flex items-start gap-1.5 px-2 py-1 rounded text-[11px] font-semibold text-amber-800 dark:text-amber-200 bg-amber-500/10'
                          : 'flex items-start gap-1.5 px-2 py-1 rounded text-[11px] font-semibold text-violet-800 dark:text-violet-200 bg-violet-500/10'
                      }
                      aria-current="page"
                    >
                      <span aria-hidden="true" className="mt-0.5">→</span>
                      <span className="leading-tight">{p.title}</span>
                    </div>
                  ) : (
                    <Link
                      to={p.to}
                      data-testid={`learn-sidebar-guide-${p.to.split('/').pop()}`}
                      className="block px-2 py-1 rounded text-[11px] text-foreground/70 hover:text-foreground hover:bg-muted/50 leading-tight transition-colors"
                    >
                      {p.title}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  // Round 97k (Feb 2026) — In index mode, drop the generic
  // "On this page" heading. Instead, mirror the visual grouping used
  // for Non-Dividend (amber) by prefixing the dividend-section list
  // with a violet "Dividend" label. Left-aligned to the same
  // px-2.5 gutter as the section chips so it visually anchors to the
  // Basics row directly beneath it. Guide mode retains its explicit
  // "Learn Guide Sections" heading.
  const isIndexMode = mode === 'index';

  return (
    <div>
      {!isIndexMode && (
        <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-semibold mb-3">
          {headingText}
        </p>
      )}
      <ul className="space-y-1.5">
        {isIndexMode && (
          <li>
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-violet-600/85 dark:text-violet-300/70 font-semibold mb-2 px-2.5"
              data-testid="learn-sidebar-dividend-header"
            >
              Dividend
            </p>
          </li>
        )}
        {SECTIONS.map((s) => renderSection(s, 'violet'))}
        {SHARED_SECTIONS.length > 0 && (
          <>
            <li className="pt-3 mt-2 border-t border-border/60">
              <p className="text-[10px] uppercase tracking-[0.3em] text-sky-600/85 dark:text-sky-300/70 font-semibold mb-2 px-2.5">
                Dividend &amp; Non-Dividend
              </p>
            </li>
            {SHARED_SECTIONS.map((s) => renderSection(s, 'sky'))}
          </>
        )}
        {NON_DIV_SECTIONS.length > 0 && (
          <>
            <li className="pt-3 mt-2 border-t border-border/60">
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-600/85 dark:text-amber-300/70 font-semibold mb-2 px-2.5">
                Non-Dividend
              </p>
            </li>
            {NON_DIV_SECTIONS.map((s) => renderSection(s, 'amber'))}
          </>
        )}
      </ul>
    </div>
  );
}
